import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Main from '../../Commons/Layouts/Main';
import Meta from '../../Commons/Components/Meta';
import Pagination from '../../Commons/Components/Pagination';
import { getInstructors } from './api';
import HeroBanner from './components/HeroBanner';
import DisclaimerBox from './components/DisclaimerBox';
import SortButton from './components/SortButton';
import InstructorGrid from './components/InstructorGrid';
import InstructorModal from './components/InstructorModal';

const LIMIT = 28;

const InstructorIntro = () => {
  const history = useHistory();
  const { search, state: locationState } = useLocation();

  // 뒤로가기 시 location.state에서 page/sort/scroll 복원
  const [page, setPage] = useState(locationState?.page || 1);
  const [sort, setSort] = useState(locationState?.sort || 'latest');
  const [instructors, setInstructors] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const scrollRestoredRef = useRef(false);

  const selectedId = (() => {
    const v = new URLSearchParams(search).get('modal');
    return v ? Number(v) : null;
  })();

  // URL이 ?modal= 없이 바뀔 때(뒤로가기 등) 모달 애니메이션 후 언마운트
  const [displayId, setDisplayId] = useState(selectedId);
  useEffect(() => {
    if (selectedId !== null) {
      setDisplayId(selectedId);
    } else {
      const t = setTimeout(() => setDisplayId(null), 280);
      return () => clearTimeout(t);
    }
  }, [selectedId]);

  const fetchList = useCallback(async (nextPage, nextSort) => {
    setLoading(true);
    try {
      const data = await getInstructors({ page: nextPage, limit: LIMIT, sort: nextSort });
      setInstructors(data.instructors || []);
      setTotal(data.total || 0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList(page, sort);
  }, [page, sort, fetchList]);

  // 데이터 로드 완료 후 스크롤 복원
  useEffect(() => {
    if (!loading && !scrollRestoredRef.current && locationState?.scroll) {
      window.scrollTo(0, locationState.scroll);
      scrollRestoredRef.current = true;
    }
  }, [loading, locationState]);

  const handleSortChange = (value) => {
    setSort(value);
    setPage(1);
  };

  const handlePaginate = (nextPage) => {
    setPage(nextPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openModal = (id) => {
    history.push({
      pathname: '/instructors',
      search: `?modal=${id}`,
      state: { page, sort, scroll: window.scrollY },
    });
  };

  return (
    <Main>
      <Meta
        metaInfo={{
          title: '강사 소개 | 엔터잡에듀',
          description:
            '엔터테인먼트 업계 현직자로 구성된 엔터잡에듀 강사진을 소개합니다. SM, HYBE, YG, JYP 등 대형 엔터사 출신 강사들의 검증된 커리어로 차별화된 강의를 경험하세요.',
          keywords:
            '엔터잡에듀 강사, 엔터테인먼트 강사, 엔터테인먼트 취업 강의, SM 출신 강사, HYBE 강사, 현직자 강의, 엔터테인먼트 교육',
          canonical: 'instructors',
        }}
      />

      <div className="w-full pt-[24px] pb-[60px] px-4 md:pt-[42px] md:pb-[137px] md:px-0">
        <div className="max-w-[1200px] mx-auto flex flex-col gap-[16px]">
          <h2 className="font-pretendard font-bold text-[24px] leading-[36px] tracking-[-0.4px] text-black m-0">
            엔터잡에듀 강사 소개
          </h2>

          <div className="flex flex-col gap-[8px]">
            <HeroBanner />
            <DisclaimerBox />

            <div className="h-[75px] flex items-center justify-end">
              <SortButton value={sort} onChange={handleSortChange} />
            </div>

            {loading ? (
              <div className="py-20 text-center font-pretendard text-[20px] text-[#888]">불러오는 중...</div>
            ) : (
              <InstructorGrid instructors={instructors} onCardClick={openModal} />
            )}

            <div className="py-12 flex justify-center">
              <Pagination total={total} limit={LIMIT} currentPage={page} paginate={handlePaginate} />
            </div>
          </div>
        </div>
      </div>

      {displayId != null && (
        <InstructorModal
          instructorId={displayId}
          isOpen={selectedId !== null}
          onClose={() => history.replace('/instructors')}
        />
      )}
    </Main>
  );
};

export default InstructorIntro;
